import React from "react";
import useLogin from "@common/components/hooks/useLogin";
import { Controller } from "react-hook-form";
import { DateInput } from "semantic-ui-calendar-react";
import { Form, Label } from "semantic-ui-react";

/**
 * DateComponent renders a date component with validation.
 *
 * @typedef {object} InputProps
 * @property {Function} control - Function to control the date field for form
 * @property {string} name - Name of the date field.
 * @property {string} [label=''] - Label of the date field.
 * @property {string} placeholder - Placeholder text of the date field.
 * @property {Object} [error=null] - Object containing validation error.
 * @property {boolean} [labelAbove=false] - Boolean to determine if the label is above or next to the date field.
 * @property {string} [dateFormat='DD MM YYYY'] - The format of the date.
 * @property {string} [maxDate=null] - The maximum date possible to select.
 * @property {string} [startMode='day'] - The start mode of the date popop: day, month, year.
 * @property {string} [initialDate=''] - The start date of the popup.
 * @property {string} [fieldStyle='flex-row-center'] - Style for Form.Field.
 * @property {string} [labelStyle=''] - Style for the label.
 * @property {string} [dateContainerStyle='w-100'] - Style for date field container.
 *
 * @component
 * @param {InputProps} props - The component props.
 * @returns {JSX.Element} The rendered date input field with validation.
 */

export default function DateComponent({
  control,
  name,
  label = "",
  placeholder = "",
  error = null,
  labelAbove = false,
  dateFormat = "DD MM YYYY",
  maxDate = null,
  startMode = "day",
  initialDate = "",
  fieldStyle = "flex-row-center",
  labelStyle,
  dateContainerStyle = "w-100",
}) {
  const [{ isInReadMode }] = useLogin();
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value } }) => (
        <Form.Field
          className={`${!labelAbove && "flex-between"} ${fieldStyle}`}
          error={!!error}
        >
          {label && (
            <label className={`${!labelAbove && "w-30"} ${labelStyle}`}>
              {label}
            </label>
          )}
          <div className={dateContainerStyle}>
            <DateInput
              value={value}
              placeholder={placeholder}
              onChange={(_, { value }) => {
                onChange(value);
              }}
              onKeyDown={(e) => e.preventDefault()}
              dateFormat={dateFormat}
              maxDate={maxDate}
              closable
              startMode={startMode}
              initialDate={initialDate}
              readOnly={isInReadMode}
              hideMobileKeyboard
              animation="fade"
              popupPosition="bottom center"
            />
            {error && (
              <Label pointing="above" prompt>
                {error?.message}
              </Label>
            )}
          </div>
        </Form.Field>
      )}
    />
  );
}
